// pkg = { Package, Destination, Attributes, Itineraries, Files }

import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MapPin,
  CalendarDays,
  Clock,
  Paperclip,
  ImageOff,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Paths } from "@/src/constants/Paths";

import {
  ArrowLeft,
  X,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TourAvailability from "./TourAvailability";
import { getTourAvailPrice } from "../../actions/tour/tourAction";


const ATTRIBUTE_LABELS = {
  4: "Hotel Category",
  5: "Transport",
  6: "Meals",
  7: "Price From",
  8: "Duration",
};

function getAttrValue(attributes, keyId) {
  if (!attributes) return undefined;
  const found = attributes.find((a) => a.AttrKeyID === keyId);
  return found?.AttrValue;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
// ---------- small helpers ----------
const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
/* ────────────────────────────────────────────── */
/* Reusable sub components                       */
/* ────────────────────────────────────────────── */
function PackageHeaderInfo({
  pack,
  destinationName,
  durationText,
  priceFrom,
  onBack,
  backLabel = "Back",
}) {
  return (
    <div className="grid gap-6 md:grid-cols-1 md:items-end">
      {/* LEFT TEXT */}
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-3 w-3" /> {backLabel}
        </button>

        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
          {pack?.Title}
        </h1>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {destinationName ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3 text-primary" /> {destinationName}
            </span>
          ) : null}

          {durationText ? (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3 text-primary" /> {durationText}
            </span>
          ) : null}
        </div>

        {pack?.Summary ? (
          <p className="text-base tracking-tight">
            {pack.Summary}
          </p>
        ) : null}

        {typeof priceFrom === "number" ? (
          <div className="pt-3">
            <p className="text-sm font-semibold">
              Starting from {priceFrom.toLocaleString()}
            </p>
          </div>
        ) : null}
      </div>

      {/* RIGHT placeholder slot (keep your layout) */}
      <div />
    </div>
  );
}

// ---------- Collage component (1 big + 4 small) ----------
function TourImageCollage({ images, onPreview, onViewAll }) {
  const collage = useMemo(() => images.slice(0, 5), [images]);
  const remainingCount = Math.max(0, images.length - collage.length);

  // Track failed images by index
  const [failedImages, setFailedImages] = useState({});

  const markFailed = (idx) => {
    setFailedImages((prev) => ({ ...prev, [idx]: true }));
  };

  if (!images?.length) {
    return (
      <div className="w-full h-[360px] bg-muted border border-border rounded-2xl flex items-center justify-center text-xs text-muted-foreground">
        Image coming soon
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {images.length} photo{images.length === 1 ? "" : "s"}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={onViewAll}>
          View all photos
        </Button>
      </div>

      {/* Collage container with fixed height */}
      <div className="grid grid-cols-12 gap-2 h-[360px] overflow-hidden">
        {/* Big image */}
        <button
          type="button"
          onClick={() => !failedImages[0] && onPreview(0)}
          className={[
            "group relative col-span-12 overflow-hidden rounded-2xl border",
            "md:col-span-8 h-full",
            "bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
          ].join(" ")}
        >
          {!failedImages[0] ? (
            <img
              src={collage[0]?.FileURL}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              loading="lazy"
              onError={() => markFailed(0)}
            />
          ) : (
            <FallbackImage />
          )}

          <HoverGradient />
        </button>

        {/* 4 small images */}
        <div className="col-span-12 grid grid-cols-2 grid-rows-2 gap-2 md:col-span-4 h-full min-h-0">
          {[1, 2, 3, 4].map((slot, i) => {
            const img = collage[slot];
            const isFailed = failedImages[slot];

            if (!img?.FileURL) {
              return (
                <div
                  key={`ph-${slot}`}
                  className="h-full w-full rounded-2xl border bg-muted/40"
                />
              );
            }

            return (
              <button
                key={img.FileID ?? img.FileURL ?? slot}
                type="button"
                onClick={() => !isFailed && onPreview(slot)}
                className="group relative h-full w-full overflow-hidden rounded-2xl border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              >
                {!isFailed ? (
                  <img
                    src={img.FileURL}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                    onError={() => markFailed(slot)}
                  />
                ) : (
                  <FallbackImage />
                )}

                <HoverGradient />

                {/* +N overlay on last tile */}
                {remainingCount > 0 && i === 3 && !isFailed ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                    <div className="rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur">
                      +{remainingCount} more
                    </div>
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FallbackImage() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground text-xs">
      Image error
    </div>
  );
}

function HoverGradient() {
  return (
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
  );
}



// ---------- View all dialog ----------
function GalleryGridDialog({ open, onOpenChange, title, images, onPick }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>{title} – Photos</DialogTitle>
          <DialogDescription>Click any photo to preview.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-auto pr-1">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {images.map((img, idx) => (
              <button
                key={img.FileID ?? img.FileURL ?? idx}
                type="button"
                onClick={() => onPick(idx)}
                className="group relative overflow-hidden rounded-2xl border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              >
                <img
                  src={img.FileURL}
                  alt=""
                  className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Preview dialog ----------
function ImagePreviewDialog({
  open,
  onOpenChange,
  title,
  images,
  index,
  setIndex,
}) {
  const [imgFailed, setImgFailed] = useState(false);

  const current = images?.[index];

  useEffect(() => {
    setImgFailed(false);
  }, [index, open]);

  const prevImage = () => setIndex((i) => Math.max(i - 1, 0));
  const nextImage = () => setIndex((i) => Math.min(i + 1, images.length - 1));

  const canPrev = index > 0;
  const canNext = index < images.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl p-0">
        <div className="relative">
          {/* Top bar */}
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{title}</div>
              <div className="text-xs text-muted-foreground">
                {index + 1} of {images.length}
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative flex items-center justify-center bg-black min-h-[50vh]">
            {current?.FileURL && !imgFailed ? (
              <img
                src={current.FileURL}
                alt=""
                className="max-h-[75vh] w-auto max-w-full object-contain"
                loading="eager"
                onError={() => setImgFailed(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                  <ImageOff className="h-6 w-6 text-white/80" />
                </div>
                <div className="text-sm font-medium text-white">
                  No preview available
                </div>
                <div className="text-xs text-white/70">
                  This image could not be loaded.
                </div>
              </div>
            )}

            {/* Prev / Next overlay buttons */}
            {images.length > 1 ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={prevImage}
                  disabled={!canPrev}
                  className={[
                    "absolute left-3 top-1/2 -translate-y-1/2",
                    "h-10 w-10 rounded-full",
                    "bg-black/40 text-white hover:bg-black/55",
                    "border border-white/15 shadow-md backdrop-blur-sm",
                    "disabled:opacity-40 disabled:hover:bg-black/40",
                  ].join(" ")}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={nextImage}
                  disabled={!canNext}
                  className={[
                    "absolute right-3 top-1/2 -translate-y-1/2",
                    "h-10 w-10 rounded-full",
                    "bg-black/40 text-white hover:bg-black/55",
                    "border border-white/15 shadow-md backdrop-blur-sm",
                    "disabled:opacity-40 disabled:hover:bg-black/40",
                  ].join(" ")}
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>

                <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-black/35 to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-black/35 to-transparent" />
              </>
            ) : null}
          </div>

          {/* Thumbnails */}
          <div className="border-t bg-background px-4 py-3">
            <div className="flex gap-2 overflow-x-auto">
              {images.map((img, idx) => (
                <button
                  key={`thumb-${img.FileID ?? img.FileURL ?? idx}`}
                  type="button"
                  onClick={() => setIndex(idx)}
                  className={[
                    "shrink-0 overflow-hidden rounded-md border",
                    idx === index ? "border-primary" : "border-border",
                  ].join(" ")}
                  aria-label={`Open image ${idx + 1}`}
                >
                  <img
                    src={img.FileURL}
                    alt=""
                    className="h-14 w-14 object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- MAIN: your cleaned PackageHeader ----------
function PackageHeader({ pack, destination, attributes, files }) {
  const location = useLocation();
  const search = location.search;
  const navigate = useNavigate();

  // Check if user came from search page by checking if there's a search query
  // or if the referrer includes the search path
  const cameFromSearch = search && search.length > 0;

  const durationText = getAttrValue(attributes, 8);
  const priceFrom = safeNumber(getAttrValue(attributes, 7));
  const destinationName = destination?.DestinationName;

  // Images (no mutation)
  const images = useMemo(() => {
    const list = Array.isArray(files) ? [...files] : [];

    if (destination?.ImageUrl) {
      list.unshift({ FileID: -1, FileURL: destination.ImageUrl, FileType: "image" });
    }

    const onlyImages = list.filter((f) => f?.FileURL?.match(/\.(jpg|jpeg|png|webp)$/i));

    const seen = new Set();
    return onlyImages.filter((x) => {
      if (!x?.FileURL) return false;
      if (seen.has(x.FileURL)) return false;
      seen.add(x.FileURL);
      return true;
    });
  }, [files, destination?.ImageUrl]);

  const [allOpen, setAllOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openPreview = (idx) => {
    if (!images[idx]) return;
    setPreviewIndex(idx);
    setPreviewOpen(true);
  };

  // Handle back navigation based on where user came from
  const handleBack = () => {
    if (cameFromSearch) {
      // Navigate back to search with preserved search params and hash
      navigate({
        pathname: Paths.TOUR_SEARCH,
        search,
        hash: `#${pack?.PackageID}`,
      });
    } else {
      // Just go back in browser history
      navigate(-1);
    }
  };

  const backLabel = cameFromSearch ? "Back to Search" : "Go Back";

  return (
    <section className="relative w-full border-b border-border bg-background/60">
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-6 space-y-4">
        <PackageHeaderInfo
          pack={pack}
          destinationName={destinationName}
          durationText={durationText}
          priceFrom={priceFrom}
          onBack={handleBack}
          backLabel={backLabel}
        />

        <TourImageCollage
          images={images}
          onPreview={openPreview}
          onViewAll={() => setAllOpen(true)}
        />
      </div>

      <GalleryGridDialog
        open={allOpen}
        onOpenChange={setAllOpen}
        title={pack?.Title || "Photos"}
        images={images}
        onPick={(idx) => {
          setAllOpen(false);
          openPreview(idx);
        }}
      />

      <ImagePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={pack?.Title || "Preview"}
        images={images}
        index={previewIndex}
        setIndex={setPreviewIndex}
      />
    </section>
  );
}

function PackageAttributes({ attributes }) {
  if (!attributes || attributes.length === 0) return null;

  // Filter to only show Days (AttrKeyID 1) and Group Size (AttrKeyID 2)
  const filteredAttrs = attributes.filter(
    (attr) => attr.AttrKeyID === 1 || attr.AttrKeyID === 2
  );

  if (filteredAttrs.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-6 py-4 border-b border-border">
      {filteredAttrs.map((attr) => (
        <div key={`${attr.AttrKeyID}-${attr.AttrValue}`} className="space-y-1">
          <p className="text-lg font-semibold text-foreground">
            {attr.AttrValue} {attr.AttrKey === "Days" ? "days" : "people"}
          </p>
          <p className="text-sm text-muted-foreground">
            {attr.AttrKey === "Days" ? "Duration" : "Maximum group size"}
          </p>
        </div>
      ))}
    </div>
  );
}

// Itinerary Image Gallery Component (Horizontal Scrollable), this is for the itinerary images only, the destination gallary has it's own code
function ItineraryGallery({ files, title }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!files || files.length === 0) return null;

  const openPreview = (idx) => {
    setPreviewIndex(idx);
    setPreviewOpen(true);
  };

  // Single image - compact display
  if (files.length === 1) {
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={() => openPreview(0)}
          className="group relative w-full aspect-video overflow-hidden rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <img
            src={files[0].FileURL}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
            onError={(e) => {
              e.target.parentElement.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute bottom-2 left-2 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Click to enlarge
          </div>
        </button>
        <ImagePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          title={title}
          images={files}
          index={previewIndex}
          setIndex={setPreviewIndex}
        />
      </div>
    );
  }

  // Multiple images - horizontal scrollable gallery
  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-foreground">
          {files.length} Photos
        </p>
      </div>

      {/* Horizontal scrollable container */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
        {files.map((file, idx) => (
          <button
            key={file.FileID || idx}
            type="button"
            onClick={() => openPreview(idx)}
            className="group relative flex-shrink-0 w-40 h-28 overflow-hidden rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <img
              src={file.FileURL}
              alt=""
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.05]"
              loading="lazy"
              onError={(e) => {
                e.target.parentElement.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {idx + 1}/{files.length}
            </div>
          </button>
        ))}
      </div>

      <ImagePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={title}
        images={files}
        index={previewIndex}
        setIndex={setPreviewIndex}
      />
    </div>
  );
}

function TripDetails({ itineraries, inclusionExclusion }) {
  return (
    <Card className="border border-border bg-card shadow-sm">
      <CardHeader>
        <CardTitle className="text-sm md:text-base">
          Trip details
        </CardTitle>
        <CardDescription className="text-xs text-muted-foreground">
          Day-by-day itinerary and specific inclusions.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue="itinerary" className="w-full">
          <TabsList className="h-8 bg-accent border border-border px-1">
            <TabsTrigger
              value="itinerary"
              className="text-[11px]"
            >
              Itinerary
            </TabsTrigger>
            <TabsTrigger
              value="inclusions"
              className="text-[11px]"
            >
              Inclusions
            </TabsTrigger>
          </TabsList>

          {/* Itinerary */}
          <TabsContent value="itinerary" className="pt-3">
            {itineraries && itineraries.length > 0 ? (
              <Accordion type="single" collapsible>
                {itineraries.map((item, index) => {
                  // const start = item.TripStartDate
                  //   ? formatDate(item.TripStartDate)
                  //   : "";
                  // const end = item.TripEndDate
                  //   ? formatDate(item.TripEndDate)
                  //   : "";
                  const days = item.TotalDays || "";
                  // const dateRange =
                  //   start || end ? `${start || "N/A"} – ${end || "N/A"}` : null;

                  return (
                    <AccordionItem
                      key={item.ItineraryID || index}
                      value={`day-${index + 1}`}
                    >
                      <AccordionTrigger className="text-sm py-2">
                        <span className="flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center bg-primary/10 text-xs font-medium text-primary">
                            {index + 1}
                          </span>
                          <span className="text-left">
                            {item.Title || `Day ${index + 1}`}
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground space-y-3">
                        {/* Itinerary Image Gallery */}
                        <ItineraryGallery
                          files={item.FileList}
                          title={item.Title || `Day ${index + 1}`}
                        />

                        {item.Destination && (
                          <p>
                            <span className="font-semibold text-foreground">
                              Destination:&nbsp;
                            </span>
                            {item.Destination}
                          </p>
                        )}

                        {/* {dateRange && (
                          <p>
                            <span className="font-semibold text-foreground">
                              Dates:&nbsp;
                            </span>
                            {dateRange}
                          </p>
                        )} */}
                        {days && (
                          <p>
                            <span className="font-semibold text-foreground">
                              Duration:&nbsp;
                            </span>
                            {days} Days
                          </p>
                        )}

                        {item.ActivityInfo && (
                          <div className="pt-1">
                            <span className="font-semibold text-foreground">
                              Activities:
                            </span>
                            <ul className="list-disc pl-5 mt-1 space-y-0.5">
                              {item.ActivityInfo.split(',').map((activity, i) => (
                                <li key={i} className="pl-1">
                                  {activity.trim()}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.MealInfo && (
                          <p>
                            <span className="font-semibold text-foreground">
                              Meals:&nbsp;
                            </span>
                            {item.MealInfo}
                          </p>
                        )}

                        {item.Summary && <p>
                          <span className="font-semibold text-foreground">
                            Summary:&nbsp;
                          </span>
                          {item.Summary}</p>}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : (
              <p className="text-xs text-muted-foreground">
                Itinerary information not available for this tour.
              </p>
            )}
          </TabsContent>

          {/* Inclusions / Exclusions */}
          <TabsContent value="inclusions" className="pt-3">
            {inclusionExclusion ? (
              <div className="space-y-6 text-sm">
                {inclusionExclusion.Inclusion && (
                  <div>
                    <h4 className="font-semibold mb-2 text-foreground">Inclusions</h4>
                    <div
                      className="prose prose-sm max-w-none text-muted-foreground [&>ul]:list-disc [&>ul]:pl-5 [&>p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: inclusionExclusion.Inclusion }}
                    />
                  </div>
                )}
                {inclusionExclusion.Exclusion && (
                  <div>
                    <h4 className="font-semibold mb-2 text-foreground">Exclusions</h4>
                    <div
                      className="prose prose-sm max-w-none text-muted-foreground [&>ul]:list-disc [&>ul]:pl-5 [&>p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: inclusionExclusion.Exclusion }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Inclusions and exclusions detailed information not available.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function QuickInfo({ destination, attributes }) {
  const durationText = getAttrValue(attributes, 8);
  const hotelCategory = getAttrValue(attributes, 4);
  const transport = getAttrValue(attributes, 5);
  const meals = getAttrValue(attributes, 6);

  const destinationName = destination?.DestinationName;

  return (
    <Card className="border border-border bg-card shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Quick info
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs text-muted-foreground">
        {durationText && (
          <div className="flex items-center gap-2">
            <CalendarDays className="h-3 w-3 text-primary" />
            <span>{durationText}</span>
          </div>
        )}
        {destinationName && (
          <div className="flex items-center gap-2">
            <MapPin className="h-3 w-3 text-primary" />
            <span>{destinationName}</span>
          </div>
        )}
        {hotelCategory && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">Hotel:</span>
            <span>{hotelCategory}</span>
          </div>
        )}
        {meals && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">Meals:</span>
            <span>{meals}</span>
          </div>
        )}
        {transport && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">Transport:</span>
            <span>{transport}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3 text-primary" />
          <span>Instant enquiry response during business hours.</span>
        </div>
      </CardContent>
    </Card>
  );
}
function Downloads({ files }) {
  // Guard
  if (!Array.isArray(files) || files.length === 0) return null;

  // Only keep NON-image files
  const downloadableFiles = files.filter((file) => {
    if (!file?.FileURL) return false;

    // Filter out images by extension
    return file.FileType !== "image" && !file.FileURL.match(/\.(jpg|jpeg|png|webp)$/i);
  });

  // If no downloadable files, do not render
  if (downloadableFiles.length === 0) return null;

  return (
    <Card className="border border-border bg-card shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Downloads</CardTitle>
        <CardDescription className="text-[11px] text-muted-foreground">
          Brochures, PDFs, or detailed itineraries.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-2 text-xs text-muted-foreground">
        {downloadableFiles.map((file) => (
          <a
            key={file.FileID ?? file.FileURL}
            href={file.FileURL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-accent border border-border hover:border-primary/60 hover:bg-accent/80 transition-colors"
          >
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {file.FileType || "Itinerary file"}
            </span>
            <span className="text-[11px] text-primary">View</span>
          </a>
        ))}
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────── */
/* Main component                                */
/* ────────────────────────────────────────────── */

function PackageDetail({ pkg }) {
  const { Package: pack, Destination, Attributes, Itineraries, Files, InclusionExclusion } = pkg;
  const [availOptions, setAvailOptions] = useState([]);
  const [loadingPrice, setLoadingPrice] = useState(false);

  useEffect(() => {
    if (pack?.PackageID) {
      setLoadingPrice(true);
      getTourAvailPrice(pack.PackageID, (data) => {
        setLoadingPrice(false);
        if (data && Array.isArray(data)) {
          setAvailOptions(data);
        } else if (data) {
          setAvailOptions([data]);
        }
      });
    }
  }, [pack?.PackageID]);

  const lowestPrice = useMemo(() => {
    if (!availOptions || availOptions.length === 0) return null;
    const prices = availOptions.map(item => (item.TourFare || 0) + (item.FlightFare || 0));
    return Math.min(...prices);
  }, [availOptions]);

  return (
    <div className="w-full min-h-screen bg-background text-foreground">
      <PackageHeader
        pack={pack}
        destination={Destination}
        attributes={Attributes}
        files={Files}
      />

      <section className="max-w-6xl mx-auto px-4 py-6 grid gap-5 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* Left Column */}
        <div className="space-y-6">
          <PackageAttributes attributes={Attributes} />

          {pack?.Highlights && (
            <div className="pt-4 space-y-3">
              <h2 className="text-lg font-semibold">Highlights</h2>
              <div
                className="prose max-w-none tracking-tight [&>ol]:list-decimal [&>ol]:pl-5 [&>ul]:list-disc [&>ul]:pl-5 [&_li]:mb-2"
                dangerouslySetInnerHTML={{ __html: pack.Highlights }}
              />
            </div>
          )}

          <TripDetails
            itineraries={Itineraries}
            inclusionExclusion={InclusionExclusion}
          />
          <div id="tour-availability">
            <TourAvailability
              packageId={pack?.PackageID}
              durationDays={getAttrValue(Attributes, 8)}
              packageTitle={pack?.Title}
              itineraries={Itineraries}
              destination={Destination}
              initialData={availOptions}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <QuickInfo
            destination={Destination}
            attributes={Attributes}
          />
          <Downloads files={Files} />
          <div className="border border-border rounded-lg p-4 space-y-3">
            {lowestPrice !== null && (
              <div className="text-center pb-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Starting from</p>
                <p className="text-2xl font-bold text-primary">NPR {lowestPrice.toLocaleString()}</p>
              </div>
            )}
            <Button onClick={handleCheckAvailability} className="w-full bg-primary text-primary-foreground h-12">
              <span>Check Availability</span>
            </Button>
            <Button className="w-full bg-secondary text-secondary-foreground hover:bg-green-200/80 hover:text-primary h-12" onClick={handleMakeEnquiry}>
              <span>Make an Enquiry</span>
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
}
function handleCheckAvailability() {
  const availabilitySection = document.getElementById("tour-availability");
  if (availabilitySection) {
    availabilitySection.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
function handleMakeEnquiry() {
  window.location.href = Paths.CONTACT_US;
}



export default PackageDetail;
